import { LocalDate } from "@js-joda/core";
import { DataEntitiesWaitlistEntry } from "../shared/api/axios-client";

export interface OperatorApi {
    operatorId: number;
    name: string;
    address: UsAddressApi;
    emailAddress: string;
    phoneNumber: string;
    website: string;
    logoUrl: string;
    topTitle: string;
    topText: string;
    bottomLeftTitle: string;
    bottomLeftText: string;
    bottomRightTitle: string;
    bottomRightText: string;
}

export interface FacilityInfoApi {
    facilityId: number;
    operatorId: number;
    name: string;
    description: string;
    timeZone: string;
    address: UsAddressApi;
    coordinates: CoordinatesApi;
    listImageId: number | null;
    entryInstructions: string;
    phoneNumber: string;
    emailAddress: string;
    clearanceInches: number;
    isWaitlistEnabled: boolean;
    hasWaitlistEntries: boolean;
}

export interface UsAddressApi {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface CoordinatesApi {
    lat: number;
    lng: number;
}

export interface OrderDebtApi {
    orderId: number;
    createdWhen: string;
    orderTotal: number;
    paid: number;
    unpaid: number;
}

export interface VehicleApi {
    vehicleId: number;
    make: string;
    model: string;
    color: string;
    stateCode: string;
    licensePlateNumber: string;
    parkerId: number;
}

export interface RateProgramApi {
    rateProgramId: number;
    facilityId: number;
    name: string;
    start: string;
    end: string | null;
    inAfter: string | null;
    inBefore: string | null;
    outAfter: string | null;
    outBefore: string | null;
    isEvent: boolean;
    isValidation: boolean;
    entries: RateEntryApi[];
    isActive: boolean;
}

export interface RateEntryApi {
    rateEntryId: number;
    rateProgramId: number;
    upTo: number;
    rate: number;
}

export interface FacilityImageApi {
    facilityImageId: number;
    storedImageId: number;
    orderId: number;
    caption: string;
    altText: string;
}

export interface ListedPlanApi {
    planId: number;
    planEntryId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    price: number;
    isOneTermPlan: boolean;
}

export interface OrderApi {
    orderId: number;
    initiator: OrderInitiator;
    initiatedByStaffId: number | null;
    parkerId: number;
    facilityId: number;
    price: number;
    createdWhen: string;
    processorPaymentIntentKey: string | null;
    type: OrderType;
    facility: FacilityInfoApi;
    submittedWhen: string | null;
    balance: OrderBalanceApi;
    summary: string;
}

export interface OrderDetailsApi {
    order: OrderApi;
    balanceChanges: OrderBalanceApi[];
    summary: string;
}

export interface OrderBalanceApi {
    orderBalanceId: number;
    type: OrderBalanceType;
    orderId: number;
    createdWhen: string;
    debt: number;
    paid: number;
    balance: number;
    totalDebt: number;
    totalPaid: number;
    reason: string | null;
    paymentId: number | null;
}

export interface InvoiceApi {
    invoiceId: number;
    invoiceNumber: string;
    orderId: number;
    createdWhen: string;
    dueWhen: string;
    openingBalance: number;
    amountDue: number;
    endingBalance: number;
    emailSentWhen: string | null;
    facility: FacilityInfoApi;
    order: OrderInfoApi;
    parker: ParkerInfoApi;
}

export interface PaymentApi {
    paymentId: number;
    parentPaymentId: number | null;
    facilityId: number;
    facility: FacilityInfoApi;
    type: PaymentType;
    amount: number;
    remaining: number;
    unapplied: number;
    paymentMethod: string;
    paymentIntentKey: string | null;
    chargeKey: string | null;
    paymentKey: string | null;
    changes: PaymentChangeApi[];
    applications: ApplyPaymentApi[];
    createdWhen: string;
    paidWhen: string;
}

export interface ApplyPaymentApi {
    orderBalanceId: number;
    type: OrderBalanceType;
    facilityId: number;
    parkerId: number;
    orderId: number;
    debt: number;
    paid: number;
    balance: number;
    totalDebt: number;
    totalPaid: number;
    createdWhen: string;
}

export interface OrderInfoApi {
    orderId: number;
    initiator: OrderInitiator;
    initiatedByStaffId: number | null;
    parkerId: number;
    facilityId: number;
    price: number;
    createdWhen: string;
    processorPaymentIntentKey: string | null;
    orderType: OrderType;
    submittedWhen: string | null;
    balance: OrderBalanceApi;
    summary: string;
}

export interface PaymentChangeApi {
    paymentId: number;
    parentPaymentId: number | null;
    facilityId: number;
    parkerId: number;
    type: PaymentType;
    amount: number;
    balance: number;
    createdWhen: string;
}

export interface SignUpReqApi {
    name: string;
    emailAddress: string;
    phoneNumber: string;
    password: string;
    passwordConfirm: string;
}

export interface LogInReqApi {
    emailAddress: string;
    password: string;
}

export interface ChangeEmailReqApi {
    currentPassword: string;
    newEmailAddress: string;
}

export interface ChangePasswordReqApi {
    currentPassword: string;
    newPassword: string;
}

export interface ForgotPasswordReqApi {
    emailAddress: string;
}

export interface ResetPasswordReqApi {
    resetPasswordCode: string;
    newPassword: string;
}

export interface ResetCodeCheckReqApi {
    resetPasswordCode: string;
}

export interface FacilityApi {
    facilityId: number;
    name: string;
    description: string;
    timeZone: string;
    address: UsAddressApi;
    coordinates: CoordinatesApi;
    listImageId: number | null;
    entryInstructions: string;
    physicalCapacity: number;
    enforcedCapacity: number;
    clearanceInches: number;
    website: string;
    emailAddress: string;
    phoneNumber: string;
    images: FacilityImageApi[];
    hourlyRates: RateProgramApi[];
    listedPlans: ListedPlanApi[];
    features: string[];
    isWaitlistEnabled: boolean;
}

export interface ParkerApi {
    parkerId: number;
    operatorId: number;
    name: string;
    emailAddress: string;
    phoneNumber: string;
    createdWhen: string;
}

export interface SubscriptionApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    start: string;
    facility: FacilityInfoApi;
    accessPeriods: AccessPeriodInfoApi[];
    parkers: SubscriptionParkerApi[];
    vehicles: VehicleApi[];
    paymentMethod: string | null;
    planEntryId: number;
    planPriceId: number;
    quantity: number;
    end: string | null;
    isAccountsReceivable: boolean;
    price: number;
    plan: PlanInfoApi;
    isEnhancedGroup: boolean;
}

export interface PlanInfoApi {
    planId: number;
    facilityId: number;
    name: string;
    description: string;
    accessHours: string;
    dayOfMonthInvoice: number;
    dayOfMonthDue: number;
    dayOfMonthLate: number;
    isOneTermParking: boolean;
}

export interface ReservationApi {
    reservationId: number;
    facilityId: number;
    parkerId: number;
    start: string;
    end: string;
    actualStart: string;
    actualEnd: string;
    duration: number;
    rateProgramId: number;
    price: number;
    createdWhen: string;
    facility: FacilityInfoApi;
    rateProgram: RateProgramApi;
}

export interface OrderFeeCalculationApi {
    amount: number;
    name: string;
    isPaidByParker: boolean;
    operatorFeeId: number;
}

export interface ReserveInitReqApi {
    facilityId: number;
    start: string;
    end: string;
    guestEmail: string | null;
    rateProgramId: number | null;
    vehicle: VehicleApi | null;
    spaces: number | null;
    selectedOptInFees?: number[];
    validationRequestId?: string;
}

export interface ReserveInitRespApi {
    facilityId: number;
    start: string;
    end: string;
    stayDuration: number;
    chargeDuration: number;
    orderId: number;
    price: number;
    customerSessionClientSecret: string;
    paymentIntentClientSecret: string;
    selectedOptInFees?: number[];
}

export interface ReservePreviewRespApi {
    fees: OrderFeeCalculationApi[];
    price: number;
}

export interface ReserveConfirmReqApi {
    orderId: number;
}

export interface ReserveConfirmRespApi {
    statusMsg: string;
}

export interface SubscribeInitReqApi {
    facilityId: number;
    planEntryId: number;
    start: string;
    quantity: number;
    isAch: boolean;
    couponPriceId?: number | null;
    selectedOptInFees?: number[];
}

export interface SubscribeInitRespApi {
    facilityId: number;
    planEntryId: number;
    start: string;
    quantity: number;
    price: number;
    orderId: number;
    paymentIntentClientSecret: string;
    customerSessionClientSecret: string;
    waitlistEntryId: number;
}

export interface SubscribeConfirmReqApi {
    orderId: number;
}

export interface SubscribeConfirmRespApi {
    status: string;
}

export interface SubscribeUpdateReqApi {
    facilityId: number;
    subscriptionId: number;
    newPlanEntryId: number;
    newQuantity: number;
}

export interface QuantityUpdateReqApi {
    newQuantity: number;
    subscriptionId: number;
}

export interface SubscribeUpdateInitRespApi {
    facilityId: number;
    planEntryId: number;
    start: string;
    quantity: number;
    price: number;
    orderId: number | null;
    paymentIntentClientSecret: string | null;
    customerSessionClientSecret: string | null;
    waitlistEntryId: number;
}

export interface SubscribeUpdateDecreaseRespApi {
    subscriptionEntryId: number;
}

export interface SubscribeUpdateDeferRespApi {
    subscriptionEntryId: number;
    orderId: number;
}

export interface SubscribeUpdateConfirmReqApi {
    orderId: number;
}

export interface SubscribeUpdateConfirmRespApi {
    status: string;
}

export interface OrderPayInitReqApi {
    orderId: number;
}

export interface OrderPayInitRespApi {
    orderId: number;
    price: number;
    paymentIntentClientSecret: string;
    customerSessionClientSecret: string;
}

export interface SettleInitReqApi {
    facilityId: number;
    isAch: boolean;
    amountInCents?: number;
}

export interface SettleInitRespApi {
    facility: FacilityInfoApi;
    orders: OrderApi[];
    price: number;
    totalPrice: number;
    paymentIntentClientSecret: string;
    customerSessionClientSecret: string;
    fees: OrderFeeCalculationApi[];
}

export interface FacilityParkerBalanceApi {
    facility: FacilityInfoApi;
    balance: number;
    orderDebts: OrderDebtApi[];
}

export interface AccessPeriodInfoApi {
    accessPeriodId: number;
    subscriptionEntryId: number;
    quantity: number;
    start: string;
    end: string;
    isDelivered: boolean;
    price: number;
}

export interface SubscriptionParkerInviteReqApi {
    subscriptionId: number;
    quantity: number;
    name: string;
    emailAddress: string;
    isLoginEnabled: boolean;
    subscriptionGroupId: number | null;
}

export interface SubscriptionParkerChangeReqApi {
    parkerId: number;
    quantity: number;
    status: SubscriptionStatus;
}

export interface SubscriptionParkerRemoveReqApi {
    parkerId: number;
}

export interface VehicleAddReqApi {
    vehicleId: number;
    parkerId?: number;
}

export interface VehicleDeleteReqApi {
    vehicleId: number;
    parkerId?: number;
}

export interface VehicleLinkRespApi {
    vehicleLinkId: number;
    vehicleId: number;
    type: VehicleLinkType;
    subscriptionId: number;
}

export interface SubscriptionParkerApi {
    parker: ParkerInfoApi;
    quantity: number;
    vehicles: VehicleApi[];
    status: SubscriptionStatus;
    subscriptionId?: number;
    subscriptionGroupId?: number;
    subscriptionGroupName: string;
}

export interface ParkerInfoApi {
    parkerId: number;
    operatorId: number;
    name: string;
    emailAddress: string;
    phoneNumber: string | null;
    createdWhen: string| null;
    isManaged: boolean;
}

export interface ChangeNameReqApi {
    name: string;
    emailAddress?: string;
}

export interface ChangePhoneReqApi {
    phoneNumber: string;
}

export interface ApplyCouponToPlanRespApi {
    priceId: number;
}

export interface SubscribePreviewRespApi {
    planEntryId: number;
    start: string;
    quantity: number;
    price: number;
    fullUnitPrice: number;
    fees: OrderFeeCalculationApi[];
}

export interface SubscribePreviewReqApi {
    planEntryId: number;
    start: string;
    quantity: number;
    isAch: boolean;
    couponPriceId?: number | null;
    selectedOptInFees?: number[];
}

export interface ModifySubscriptionPreviewReqApi {
    start: string;
    quantity: number;
}

export interface SubListEntryApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    quantity: number;
    unitPrice: number | null;
    price: number | null;
    plan: PlanInfoBasicApi;
    facility: FacilityInfoApi;
    start: LocalDate;
}

export interface SubscriptionGuestApi {
    subscriptionId: number;
    parkerId: number;
    status: SubscriptionStatus;
    start: string;
    quantity: number;
    parker: ParkerInfoApi;
    plan: PlanInfoBasicApi;
    facility: FacilityInfoApi;
    vehicles: VehicleApi[];
    accessPeriods: AccessPeriodInfoApi[];
}

export interface PlanInfoBasicApi {
    planId: number;
    planEntryId: number;
    name: string;
    accessHours: string;
    status: SubscriptionStatus;
}

export interface PaymentMethodRespApi {
    paymentMethodId: string;
    type: string;
    card: CardRespApi | null;
    usBankAccount: BankRespApi | null;
    subscriptions: SubListEntryApi[];
}

export interface CardRespApi {
    brand: string;
    last4Digits: string;
    expirationMonth: number;
    expirationYear: number;
}

export interface BankRespApi {
    bank: string;
    last4Digits: string;
}

export interface ParkerPlanInviteApi {
    parkerPlanInviteId: number;
    facilityId: number;
    listedPlan: ListedPlanApi;
    planPriceId: number;
    privatePrice: number;
    quantity: number;
    message: string;
    startDate: string;
    waitlistEntryId?: number;
}

export interface SubscribeInviteInitReqApi {
    parkerPlanInviteId: number;
    start: string;
    quantity: number;
    isAch: boolean;
    selectedOptInFees?: number[];
}

export interface SubscribeInviteInitRespApi {
    parkerPlanInviteId: number;
    start: string;
    quantity: number;
    price: number;
    orderId: number;
    paymentIntentClientSecret: string;
    customerSessionClientSecret: string;
    waitlistEntryId: number;
}

export interface SubscribeInvitePreviewReqApi {
    parkerPlanInviteId: number;
    start: string;
    quantity: number;
    isAch: boolean;
    selectedOptInFees?: number[];
}

export interface SubscribeInvitePreviewRespApi {
    parkerPlanInviteId: number;
    start: string;
    quantity: number;
    price: number;
    fees: OrderFeeCalculationApi[];
}


export enum OrderType {
    Imported = 5,
    Reserve = 10,
    Subscribe = 20,
    ModifySubscription = 30,
    AccessPeriod = 40,
    Custom = 50,
}

export enum PaymentType {
    ImportedPayment = 20,
    CashPayment = 40,
    AchPayment = 60,
    AchFailed = 80,
    CheckPayment = 100,
    CheckBounce = 120,
    WriteCheck = 140,
    ProcessorPayment = 160,
    ProcessorRefund = 180,
    ProcessorDisputeChange = 200,
    Adjustment = 220,
}

export enum OrderInitiator {
    System = 0,
    Parker = 1,
    Staff = 2,
}

export enum OrderBalanceType {
    OrderPlaced = 10,
    OrderFee = 20,
    ApplyPayment = 30,
    UndoApplyPayment = 40,
    RefundPayment = 50,
    OrderAdjustment = 60,
}

export enum SubscriptionStatus {
    Applied = 0,
    Rejected = 1,
    WaitingForContract = 2,
    Active = 3,
    Cancelled = 4,
    Invited = 5,
    Waitlisted = 6
}

export enum VehicleLinkType {
    Reservation = 0,
    Subscription = 1,
}


